import { apiFetch, useMockApi } from '@/lib/api/client'

const SCAN_SETTINGS_STORAGE_KEY = 'modelscout_scan_settings'

export interface ApiKeyListItem {
  id: string
  provider: { name: string; slug: string }
  keyHint?: string
  status: string
  lastTestedAt?: string
}

export interface ProviderListItem {
  id: string
  name: string
  slug: string
  isEnabled: boolean
  keyHint?: string
  keyStatus?: string
}

export interface ScanSettings {
  targetModelsPerCategory: number
  maxActivePerCategory: number
  freeOnly: boolean
  maxInputPrice?: number
  maxOutputPrice?: number
}

export interface ScanSchedule {
  scheduleCron: string
  autoEnabled: boolean
}

export const DEFAULT_SCAN_SCHEDULE: ScanSchedule = {
  scheduleCron: '0 2 * * *',
  autoEnabled: false,
}

export interface RankingWeights {
  priceScore: number
  qualityScore: number
  userScore: number
  contextScore: number
  featureScore: number
  freedomScore: number
  speedScore: number
  externalScore: number
  survivalScore: number
}

export type ThemeMode = 'light' | 'dark' | 'system'

export interface AppearanceSettings {
  theme: ThemeMode
}

export interface ClassificationSettings {
  llmEnabled: boolean
}

export const DEFAULT_RANKING_WEIGHTS: RankingWeights = {
  priceScore: 0.25,
  qualityScore: 0.2,
  userScore: 0.2,
  contextScore: 0.1,
  featureScore: 0.1,
  freedomScore: 0.05,
  speedScore: 0.05,
  externalScore: 0.05,
  survivalScore: 0,
}

export const DEFAULT_APPEARANCE: AppearanceSettings = { theme: 'system' }

export const DEFAULT_CLASSIFICATION: ClassificationSettings = { llmEnabled: false }

export interface SettingsApi {
  listProviders(): Promise<ProviderListItem[]>
  listApiKeys(): Promise<ApiKeyListItem[]>
  putApiKey(providerSlug: string, apiKey: string): Promise<{ keyHint: string; status: string }>
  testApiKey(providerSlug: string): Promise<{ success: boolean; error?: string }>
  deleteApiKey(providerSlug: string): Promise<void>
  getScanSettings(): Promise<ScanSettings>
  saveScanSettings(settings: ScanSettings): Promise<ScanSettings>
  getScanSchedule(): Promise<ScanSchedule>
  saveScanSchedule(schedule: Partial<ScanSchedule>): Promise<ScanSchedule>
  getRankingWeights(): Promise<RankingWeights>
  saveRankingWeights(weights: Partial<RankingWeights>): Promise<RankingWeights>
  getAppearance(): Promise<AppearanceSettings>
  saveAppearance(settings: AppearanceSettings): Promise<AppearanceSettings>
  getClassification(): Promise<ClassificationSettings>
  saveClassification(settings: ClassificationSettings): Promise<ClassificationSettings>
}

const DEFAULT_SCAN_SETTINGS: ScanSettings = {
  targetModelsPerCategory: 10,
  maxActivePerCategory: 30,
  freeOnly: true,
}

function loadStoredScanSettings(): ScanSettings {
  if (typeof localStorage === 'undefined') return DEFAULT_SCAN_SETTINGS

  const raw = localStorage.getItem(SCAN_SETTINGS_STORAGE_KEY)
  if (!raw) return DEFAULT_SCAN_SETTINGS

  try {
    return { ...DEFAULT_SCAN_SETTINGS, ...JSON.parse(raw) } as ScanSettings
  } catch {
    return DEFAULT_SCAN_SETTINGS
  }
}

function storeScanSettings(settings: ScanSettings) {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(SCAN_SETTINGS_STORAGE_KEY, JSON.stringify(settings))
}

class MockSettingsApi implements SettingsApi {
  private providers: ProviderListItem[] = [
    { id: 'prov-openrouter', name: 'OpenRouter', slug: 'openrouter', isEnabled: true, keyHint: '...X7k9', keyStatus: 'active' },
    { id: 'prov-groq', name: 'Groq', slug: 'groq', isEnabled: true },
  ]
  private apiKeys: ApiKeyListItem[] = [
    {
      id: 'key-openrouter',
      provider: { name: 'OpenRouter', slug: 'openrouter' },
      keyHint: '...X7k9',
      status: 'active',
      lastTestedAt: new Date().toISOString(),
    },
  ]

  async listProviders(): Promise<ProviderListItem[]> {
    return structuredClone(this.providers)
  }

  async listApiKeys(): Promise<ApiKeyListItem[]> {
    return structuredClone(this.apiKeys)
  }

  async putApiKey(providerSlug: string, apiKey: string): Promise<{ keyHint: string; status: string }> {
    const hint = apiKey.length > 4 ? `...${apiKey.slice(-4)}` : '...key'
    const existing = this.apiKeys.find((item) => item.provider.slug === providerSlug)
    if (existing) {
      existing.keyHint = hint
      existing.status = 'active'
    } else {
      this.apiKeys.push({
        id: `key-${providerSlug}`,
        provider: { name: providerSlug, slug: providerSlug },
        keyHint: hint,
        status: 'active',
      })
    }
    return { keyHint: hint, status: 'active' }
  }

  async testApiKey(_providerSlug: string): Promise<{ success: boolean; error?: string }> {
    return { success: true }
  }

  async deleteApiKey(providerSlug: string): Promise<void> {
    this.apiKeys = this.apiKeys.filter((item) => item.provider.slug !== providerSlug)
  }

  async getScanSettings(): Promise<ScanSettings> {
    return loadStoredScanSettings()
  }

  async saveScanSettings(settings: ScanSettings): Promise<ScanSettings> {
    storeScanSettings(settings)
    return settings
  }

  async getScanSchedule(): Promise<ScanSchedule> {
    return { ...DEFAULT_SCAN_SCHEDULE }
  }

  async saveScanSchedule(schedule: Partial<ScanSchedule>): Promise<ScanSchedule> {
    return { ...DEFAULT_SCAN_SCHEDULE, ...schedule }
  }

  async getRankingWeights(): Promise<RankingWeights> {
    return { ...DEFAULT_RANKING_WEIGHTS }
  }

  async saveRankingWeights(weights: Partial<RankingWeights>): Promise<RankingWeights> {
    return { ...DEFAULT_RANKING_WEIGHTS, ...weights }
  }

  async getAppearance(): Promise<AppearanceSettings> {
    return { ...DEFAULT_APPEARANCE }
  }

  async saveAppearance(settings: AppearanceSettings): Promise<AppearanceSettings> {
    return settings
  }

  async getClassification(): Promise<ClassificationSettings> {
    return { ...DEFAULT_CLASSIFICATION }
  }

  async saveClassification(settings: ClassificationSettings): Promise<ClassificationSettings> {
    return settings
  }
}

function mapRankingWeights(body: Record<string, unknown>): RankingWeights {
  return {
    priceScore: Number(body.price_score ?? DEFAULT_RANKING_WEIGHTS.priceScore),
    qualityScore: Number(body.quality_score ?? DEFAULT_RANKING_WEIGHTS.qualityScore),
    userScore: Number(body.user_score ?? DEFAULT_RANKING_WEIGHTS.userScore),
    contextScore: Number(body.context_score ?? DEFAULT_RANKING_WEIGHTS.contextScore),
    featureScore: Number(body.feature_score ?? DEFAULT_RANKING_WEIGHTS.featureScore),
    freedomScore: Number(body.freedom_score ?? DEFAULT_RANKING_WEIGHTS.freedomScore),
    speedScore: Number(body.speed_score ?? DEFAULT_RANKING_WEIGHTS.speedScore),
    externalScore: Number(body.external_score ?? DEFAULT_RANKING_WEIGHTS.externalScore),
    survivalScore: Number(body.survival_score ?? DEFAULT_RANKING_WEIGHTS.survivalScore),
  }
}

function mapScanSettings(body: Record<string, unknown>): ScanSettings {
  return {
    targetModelsPerCategory: Number(body.target_models_per_category ?? DEFAULT_SCAN_SETTINGS.targetModelsPerCategory),
    maxActivePerCategory: Number(body.max_active_models_per_category ?? DEFAULT_SCAN_SETTINGS.maxActivePerCategory),
    freeOnly: Boolean(body.free_only ?? DEFAULT_SCAN_SETTINGS.freeOnly),
    maxInputPrice: body.max_input_price != null ? Number(body.max_input_price) : undefined,
    maxOutputPrice: body.max_output_price != null ? Number(body.max_output_price) : undefined,
  }
}

class HttpSettingsApi implements SettingsApi {
  async listProviders(): Promise<ProviderListItem[]> {
    const body = await apiFetch<{ items: Array<Record<string, unknown>> }>('/api/v1/settings/providers')
    return body.items.map((item) => ({
      id: String(item.id ?? ''),
      name: String(item.name ?? ''),
      slug: String(item.slug ?? ''),
      isEnabled: Boolean(item.is_enabled ?? true),
      keyHint: item.key_hint ? String(item.key_hint) : undefined,
      keyStatus: item.key_status ? String(item.key_status) : undefined,
    }))
  }

  async listApiKeys(): Promise<ApiKeyListItem[]> {
    const body = await apiFetch<{ items: Array<Record<string, unknown>> }>('/api/v1/settings/api-keys')
    return body.items.map((item) => ({
      id: String(item.id ?? ''),
      provider: {
        name: String((item.provider as Record<string, unknown>)?.name ?? ''),
        slug: String((item.provider as Record<string, unknown>)?.slug ?? ''),
      },
      keyHint: item.key_hint ? String(item.key_hint) : undefined,
      status: String(item.status ?? ''),
      lastTestedAt: item.last_tested_at ? String(item.last_tested_at) : undefined,
    }))
  }

  async putApiKey(providerSlug: string, apiKey: string): Promise<{ keyHint: string; status: string }> {
    const body = await apiFetch<{ key_hint: string; status: string }>(
      `/api/v1/settings/api-keys/${providerSlug}`,
      {
        method: 'PUT',
        body: JSON.stringify({ api_key: apiKey }),
      },
    )
    return { keyHint: body.key_hint, status: body.status }
  }

  async testApiKey(providerSlug: string): Promise<{ success: boolean; error?: string }> {
    const body = await apiFetch<{ success: boolean; error?: string }>(
      `/api/v1/settings/api-keys/${providerSlug}/test`,
      { method: 'POST' },
    )
    return body
  }

  async deleteApiKey(providerSlug: string): Promise<void> {
    await apiFetch<void>(`/api/v1/settings/api-keys/${providerSlug}`, { method: 'DELETE' })
  }

  async getScanSettings(): Promise<ScanSettings> {
    const body = await apiFetch<Record<string, unknown>>('/api/v1/settings/scan-parameters')
    return mapScanSettings(body)
  }

  async saveScanSettings(settings: ScanSettings): Promise<ScanSettings> {
    const body = await apiFetch<Record<string, unknown>>('/api/v1/settings/scan-parameters', {
      method: 'PATCH',
      body: JSON.stringify({
        target_models_per_category: settings.targetModelsPerCategory,
        max_active_models_per_category: settings.maxActivePerCategory,
        free_only: settings.freeOnly,
        max_input_price: settings.maxInputPrice,
        max_output_price: settings.maxOutputPrice,
      }),
    })
    return mapScanSettings(body)
  }

  async getScanSchedule(): Promise<ScanSchedule> {
    const body = await apiFetch<{ schedule_cron: string; auto_enabled: boolean }>(
      '/api/v1/settings/scan-schedule',
    )
    return { scheduleCron: body.schedule_cron, autoEnabled: body.auto_enabled }
  }

  async saveScanSchedule(schedule: Partial<ScanSchedule>): Promise<ScanSchedule> {
    const body = await apiFetch<{ schedule_cron: string; auto_enabled: boolean }>(
      '/api/v1/settings/scan-schedule',
      {
        method: 'PATCH',
        body: JSON.stringify({
          schedule_cron: schedule.scheduleCron,
          auto_enabled: schedule.autoEnabled,
        }),
      },
    )
    return { scheduleCron: body.schedule_cron, autoEnabled: body.auto_enabled }
  }

  async getRankingWeights(): Promise<RankingWeights> {
    const body = await apiFetch<Record<string, unknown>>('/api/v1/settings/ranking-weights')
    return mapRankingWeights(body)
  }

  async saveRankingWeights(weights: Partial<RankingWeights>): Promise<RankingWeights> {
    const body = await apiFetch<Record<string, unknown>>('/api/v1/settings/ranking-weights', {
      method: 'PATCH',
      body: JSON.stringify({
        price_score: weights.priceScore,
        quality_score: weights.qualityScore,
        user_score: weights.userScore,
        context_score: weights.contextScore,
        feature_score: weights.featureScore,
        freedom_score: weights.freedomScore,
        speed_score: weights.speedScore,
        external_score: weights.externalScore,
        survival_score: weights.survivalScore,
      }),
    })
    return mapRankingWeights(body)
  }

  async getAppearance(): Promise<AppearanceSettings> {
    const body = await apiFetch<{ theme: ThemeMode }>('/api/v1/settings/appearance')
    return { theme: body.theme }
  }

  async saveAppearance(settings: AppearanceSettings): Promise<AppearanceSettings> {
    const body = await apiFetch<{ theme: ThemeMode }>('/api/v1/settings/appearance', {
      method: 'PATCH',
      body: JSON.stringify({ theme: settings.theme }),
    })
    return { theme: body.theme }
  }

  async getClassification(): Promise<ClassificationSettings> {
    const body = await apiFetch<{ llm_enabled: boolean }>('/api/v1/settings/classification')
    return { llmEnabled: body.llm_enabled }
  }

  async saveClassification(settings: ClassificationSettings): Promise<ClassificationSettings> {
    const body = await apiFetch<{ llm_enabled: boolean }>('/api/v1/settings/classification', {
      method: 'PATCH',
      body: JSON.stringify({ llm_enabled: settings.llmEnabled }),
    })
    return { llmEnabled: body.llm_enabled }
  }
}

export const settingsApi: SettingsApi = useMockApi() ? new MockSettingsApi() : new HttpSettingsApi()

export { DEFAULT_SCAN_SETTINGS }
